"use client"

import * as React from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast, type ToasterProps } from "sonner"
import { CircleCheckIcon, InfoIcon, TriangleAlertIcon, OctagonXIcon, Loader2Icon } from "lucide-react"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  // Injeta o botão da Lixeira no CANTO SUPERIOR DIREITO da 1ª notificação flutuante (forçando right: 0 e left: auto)
  React.useEffect(() => {
    if (typeof window === "undefined") return

    const injectClearAllButton = () => {
      const toaster = document.querySelector("[data-sonner-toaster]")
      if (!toaster) return

      const toasts = toaster.querySelectorAll("[data-sonner-toast]")

      toasts.forEach((toastEl, index) => {
        const existingBtn = toastEl.querySelector("[data-clear-all-button]")
        if (index === 0) {
          if (!existingBtn) {
            const trashBtn = document.createElement("button")
            trashBtn.setAttribute("data-clear-all-button", "true")
            trashBtn.setAttribute("type", "button")
            trashBtn.setAttribute("title", "Limpar todas as notificações flutuantes")

            // CÍRCULO no canto superior DIREITO da caixa de notificação (top: 0, right: 0, left: auto)
            trashBtn.style.setProperty("position", "absolute", "important")
            trashBtn.style.setProperty("top", "0px", "important")
            trashBtn.style.setProperty("right", "0px", "important")
            trashBtn.style.setProperty("left", "auto", "important")
            trashBtn.style.setProperty("transform", "translate(35%, -35%)", "important")
            trashBtn.style.setProperty("z-index", "999", "important")

            trashBtn.className =
              "flex size-6 items-center justify-center rounded-full border border-inherit bg-white/95 dark:bg-zinc-900/95 text-inherit shadow-md cursor-pointer transition-all hover:opacity-80"

            trashBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash-2"><path d="3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>`

            trashBtn.onclick = (e) => {
              e.stopPropagation()
              e.preventDefault()
              sonnerToast.dismiss()
            }

            toastEl.appendChild(trashBtn)
          }
        } else {
          if (existingBtn) {
            existingBtn.remove()
          }
        }
      })
    }

    const observer = new MutationObserver(injectClearAllButton)
    const container = document.body
    observer.observe(container, { childList: true, subtree: true, attributes: true })

    const interval = setInterval(injectClearAllButton, 150)

    return () => {
      observer.disconnect()
      clearInterval(interval)
    }
  }, [])

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group font-sans"
      richColors
      closeButton
      expand={false}
      icons={{
        success: (
          <div className="flex size-6 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 shrink-0">
            <CircleCheckIcon className="size-4" />
          </div>
        ),
        info: (
          <div className="flex size-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 shrink-0">
            <InfoIcon className="size-4" />
          </div>
        ),
        warning: (
          <div className="flex size-6 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 shrink-0">
            <TriangleAlertIcon className="size-4" />
          </div>
        ),
        error: (
          <div className="flex size-6 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 shrink-0">
            <OctagonXIcon className="size-4" />
          </div>
        ),
        loading: (
          <div className="flex size-6 items-center justify-center rounded-full bg-blue-100/50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 shrink-0">
            <Loader2Icon className="size-4 animate-spin text-[#004EB1] dark:text-blue-400" />
          </div>
        ),
      }}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-white/95 dark:group-[.toaster]:bg-zinc-950/95 group-[.toaster]:text-zinc-900 dark:group-[.toaster]:text-zinc-50 group-[.toaster]:border-zinc-200/80 dark:group-[.toaster]:border-zinc-800/80 font-sans shadow-xl backdrop-blur-md p-4 rounded-xl flex items-start gap-3 border transition-all duration-200",
          title: "text-zinc-900 dark:text-zinc-100 font-bold text-xs leading-snug",
          description: "text-zinc-600 dark:text-zinc-300 font-medium text-[11px] mt-0.5 leading-normal",
          success:
            "group-[.toast]:bg-emerald-50/90 dark:group-[.toast]:bg-emerald-950/40 group-[.toast]:border-emerald-200 dark:group-[.toast]:border-emerald-900/40 group-[.toast]:text-emerald-950 dark:group-[.toast]:text-emerald-200",
          error:
            "group-[.toast]:bg-red-50/90 dark:group-[.toast]:bg-red-950/40 group-[.toast]:border-red-200 dark:group-[.toast]:border-red-900/40 group-[.toast]:text-red-950 dark:group-[.toast]:text-red-200",
          warning:
            "group-[.toast]:bg-amber-50/90 dark:group-[.toast]:bg-amber-950/40 group-[.toast]:border-amber-200 dark:group-[.toast]:border-amber-900/40 group-[.toast]:text-amber-950 dark:group-[.toast]:text-amber-200",
          info:
            "group-[.toast]:bg-blue-50/90 dark:group-[.toast]:bg-blue-950/40 group-[.toast]:border-blue-200 dark:group-[.toast]:border-blue-900/40 group-[.toast]:text-blue-950 dark:group-[.toast]:text-blue-200",
          loading:
            "group-[.toast]:bg-zinc-50/90 dark:group-[.toast]:bg-zinc-900/90 group-[.toast]:border-blue-200/60 dark:group-[.toast]:border-blue-900/40",
          actionButton:
            "group-[.toast]:bg-[#004EB1] group-[.toast]:text-white dark:group-[.toast]:bg-blue-600 font-semibold text-xs rounded-lg px-3 py-1.5",
          cancelButton:
            "group-[.toast]:bg-zinc-100 group-[.toast]:text-zinc-800 dark:group-[.toast]:bg-zinc-800 dark:group-[.toast]:text-zinc-200 font-semibold text-xs rounded-lg px-3 py-1.5",
          closeButton:
            "group-[.toast]:bg-white/90 dark:group-[.toast]:bg-zinc-900/90 group-[.toast]:border-inherit group-[.toast]:text-inherit group-[.toast]:hover:opacity-80 group-[.toast]:top-0 group-[.toast]:left-0 group-[.toast]:-translate-x-1/3 group-[.toast]:-translate-y-1/3 group-[.toast]:rounded-full group-[.toast]:size-6 group-[.toast]:flex group-[.toast]:items-center group-[.toast]:justify-center group-[.toast]:shadow-md",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
